import React, {useState} from 'react';
import moment from 'moment';
import MaskedInput from 'react-maskedinput';
import { merge } from 'lodash';
import {grey, red} from 'material-colors';
import {Icon, Popover} from "@blueprintjs/core";
import { DatePicker } from "@blueprintjs/datetime";
import MomentLocaleUtils from "react-day-picker/moment";

import {primaryColor} from "../libs/theme-lib";

const DateInput = (props) => {

    // Props destructure
    const {
        className,
        style,
        labelStyle,
        inputStyle,
        inputWrapperStyle,
        iconStyle,
        labelText,
        errorText,
        helpText,
        format,
        placeholder,
        value,
        showDatePicker,
        minValue,
        maxValue,
        onChange,
        errorMessage,
        validation = false
    } = props;

    // State
    const [date, setDate] = useState(value && value.isValid() ? value.toDate() : null);
    const [isValid, setIsValid] = useState(!!value && value.isValid());
    const [datePickerIsOpen, setDatePickerIsOpen] = useState(false);

    const locale = moment.locale();

    const mask = format.replace(/D/g, '1').replace(/M/g, '1').replace(/Y/g, '1');

    const dateMinValue = minValue ? minValue.toDate() : new Date(1980, 0, 1);
    const dateMaxValue = maxValue ? maxValue.toDate() : new Date(2050, 11, 31);

    // Styles
    const styles = {
        root: {},
        inputWrapper: {
            position: 'relative',
            minWidth: 150,
            height: 42
        },
        input: {
            height: "100%"
        },
        label: {
            color: "black",
            fontWeight: "bold",
            fontSize: 12,
            marginBottom: 5,
            display: "block"
        },
        icon: {
            cursor: 'pointer',
            position: 'absolute',
            top: 10,
            right: 12,
            color: grey[700]
        }
    };
    // Merge default styles with props
    styles.root = merge(styles.root, style);
    styles.input = merge(styles.input, inputStyle);
    styles.inputWrapper = merge(styles.inputWrapper, inputWrapperStyle);
    styles.label = merge(styles.label, labelStyle);
    styles.icon = merge(styles.icon, iconStyle);
    const labelElement = labelText && <label style={styles.label}>{labelText}</label>;
    const errorElement = errorText && <span className="help-block text-danger">{errorText}</span>;
    const helpElement = helpText && <span className="help-block">{helpText}</span>;
    styles.input.borderColor = !validation || isValid ? grey[700] : red[500];
    styles.input.color = isValid ? grey[700] : red[500];
    const modifiersStyles = {
        selected: {
            color: "white",
            backgroundColor: primaryColor
        }
    };

    // Functions
    const onChangeInput = (evt) => {

        const newTextDate = evt.target.value;
        const numOfNumbers = newTextDate.replace(/[^0-9]/g,"").length;
        const newMomentDate = moment(newTextDate, format);

        if (!newTextDate) {
            setIsValid(false);
            setDate(null);
            onChange(null);
            return;
        }

        const newIsValid = newTextDate &&
            newMomentDate.isValid() &&
            newMomentDate.isAfter(moment(moment(dateMinValue).format(format), format)) &&
            newMomentDate.isBefore(moment(moment(dateMaxValue).format(format), format))
        ;

        setIsValid(newIsValid);

        if (newIsValid) setDate(moment(newTextDate, format).toDate());

        if (newIsValid && numOfNumbers === 8) onChange(moment(newTextDate, format));

    };
    const onBlur = (evt) => {

        const newTextDate = evt.target.value;
        const newMomentDate = moment(newTextDate, format);

        const newIsValid = newTextDate &&
            newMomentDate.isValid() &&
            newMomentDate.isAfter(moment(moment(dateMinValue).format(format), format)) &&
            newMomentDate.isBefore(moment(moment(dateMaxValue).format(format), format))
        ;

        setIsValid(newIsValid);

        if (newIsValid) {
            setDate( moment(evt.target.value, format).toDate());
            onChange(moment(evt.target.value, format));
        }

    };
    const onChangeDatePicker = (newDate, userClick) => {

        if (userClick) {
            onChange(moment(newDate));
            setDate(newDate);
            setIsValid(moment(newDate).isValid());
            setDatePickerIsOpen(false);
        }

    };

    return (
        <div
            className={className}
            style={styles.root}
        >
            {labelElement}
            <div style={styles.inputWrapper}>
                <MaskedInput
                    className="form-control"
                    style={styles.input}
                    placeholder={placeholder}
                    mask={mask}
                    maxLength={format.length}
                    onChange={onChangeInput}
                    onBlur={onBlur}
                    value={value && value.format(format)}
                />
                {showDatePicker ?
                    <div style={styles.icon} >
                        <Popover
                            boundary="viewport"
                            position="bottom"
                            usePortal={true}
                            autoFocus={true}
                            lazy={true}
                            canEscapeKeyClose={true}
                            isOpen={datePickerIsOpen}
                            onInteraction={(isOpen) => setDatePickerIsOpen(isOpen)}
                            content={(
                                <DatePicker
                                    locale={locale}
                                    localeUtils={MomentLocaleUtils}
                                    value={date}
                                    onChange={onChangeDatePicker}
                                    minDate={dateMinValue}
                                    maxDate={dateMaxValue}
                                    dayPickerProps={{ modifiersStyles }}
                                />
                            )}
                        >
                            <Icon
                                icon="calendar"
                                color={styles.icon.color}
                                iconSize={styles.icon.height || 20}
                            />
                        </Popover>
                    </div> : null}
            </div>
            {validation && (
                <div style={{
                    color: "red",
                    height: 18,
                    fontSize: 12
                }}>
                    {!isValid && errorMessage}
                </div>
            )}
            {helpElement}
            {errorElement}
        </div>
    );
};

DateInput.defaultProps = {
    pattern: ['d', 'm', 'Y'],
    format: 'DD/MM/YYYY',
    placeholder: 'DD/MM/YYYY',
    showDatePicker: true
};

export default DateInput;